import { FolderTreeSelect } from '../../../stories/components/CommonComponents/FolderTreeSelect/FolderTreeSelect';
import { COPY_PLAN_TO_FOLDER, COPY_PLAN_TO_PUBLICATION, FOLDER_SELECT_MENU_HEIGHT } from '@opengov/stories-shared/constants';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import { CAPTION_COPY_OF } from '../../../stories/constants';
import { IModalMessages } from '../../../types';
import { checkStoryTitleExists } from '../../../stories/routes/hasura';
import { findPublicationIdBySectionId } from '../../../stories/utils';
import { getPublicationName } from '../../../stories/common/utils';
import { useStoryFolders } from '../../../stories/components/StoryList/hooks/useStoryFolders';
import { usePublicationFoldersTree } from '../../../stories/components/StoryList/hooks/usePublicationFoldersTree';
import { CopyStoryInput, useCopyStory } from '../../../stories/components/StoryView/hooks/useStoryMutations';
import { alphabetizeNodes, compileNodes, compilePublicationNodes } from '../../../stories/utils/helper';

import { styles } from './CopyStory.styles';
export type ICopyStoryProps = {
  open: boolean;
  onClose(): void;
  entityId: number;
  entitySubdomain: string;
  storyId: string;
  storyFolderId?: string;
  publicationId?: string;
  title: string;
  messages: IModalMessages;
  closeModal(): void;
  copyStory(props: { storyId: string; destinationFolderId: string; storyTitle?: string; publicationId?: string }): Promise<void>;
};

export const CopyStory = (props: ICopyStoryProps) => {
  const {
    open,
    onClose,
    entityId,
    entitySubdomain,
    storyId,
    storyFolderId,
    publicationId,
    messages,
    title,
    closeModal,
  } = props;

  const [isCopying, setIsCopying] = useState(false);
  const [copyPlan, setCopyPlan] = useState<string>(!!publicationId ? COPY_PLAN_TO_PUBLICATION : COPY_PLAN_TO_FOLDER);
  const [storyTitle, setStoryTitle] = useState<string>(CAPTION_COPY_OF + (title ?? ''));
  const [targetFolderId, setTargetFolderId] = useState<string | undefined>(storyFolderId || undefined);
  const [titleExists, setTitleExists] = useState<boolean>(false);
  const [currentPublicationName, setCurrentPublicationName] = useState('');

  // The modal is always mounted (visibility toggled via `open`), so the initial
  // `storyTitle` is captured before `title` has loaded — leaving "Copy of undefined".
  // Re-seed the default each time the modal opens or the title resolves.
  useEffect(() => {
    if (open) {
      setStoryTitle(CAPTION_COPY_OF + (title ?? ''));
    }
  }, [open, title]);

  const foldersQuery = useStoryFolders(
    open && entityId > 0 ? { entityId, displayPublicationTab: true } : null,
    messages.somethingWentWrong
  );
  const pubFoldersQuery = usePublicationFoldersTree(
    open && entityId > 0 ? { entityId } : null,
    messages.somethingWentWrong
  );

  const folderStructure = useMemo(() => {
    const raw = foldersQuery.data?.storyFolders;
    return alphabetizeNodes(compileNodes(raw && raw[0] && raw[0].subFolders) || []);
  }, [foldersQuery.data]);

  const publicationFoldersTree = useMemo(() => {
    const raw = pubFoldersQuery.data ?? [];
    if (!raw.length) return [];
    const roots = raw.filter(
      (f) => f.storyFolder.title.includes('root-folder') && (f.storyFolder.subFolders?.length ?? 0) > 0
    );
    return alphabetizeNodes(compilePublicationNodes(roots) || []);
  }, [pubFoldersQuery.data]);

  const { mutate: doCopyStory } = useCopyStory();

  const handleSubmit = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setIsCopying(true);
    let input: CopyStoryInput = {
      entityId,
      entitySubdomain,
      storyId,
      destinationFolderId: targetFolderId!,
      storyTitle,
      messages,
    };
    if (copyPlan === COPY_PLAN_TO_PUBLICATION) {
      const pubId = findPublicationIdBySectionId(publicationFoldersTree, targetFolderId!);
      input = { ...input, publicationId: pubId };
    }
    doCopyStory(input, {
      onSettled: () => setIsCopying(false),
      onSuccess: () => onClose(),
    });
  };

  const checkTitleAvailability = useCallback(
    debounce(async (titleValue: string) => {
      if (titleValue.length > 3 && targetFolderId != null) {
        const exists = await checkStoryTitleExists(titleValue, entityId, targetFolderId);
        setTitleExists(exists);
      } else {
        setTitleExists(false);
      }
    }, 700),
    [targetFolderId]
  );

  useEffect(() => {
    void checkTitleAvailability(storyTitle);
  }, [storyTitle, copyPlan, checkTitleAvailability]);

  useEffect(() => {
    const publicationName = getPublicationName(copyPlan, targetFolderId ?? '', publicationFoldersTree);
    setCurrentPublicationName(publicationName);
  }, [targetFolderId, copyPlan, publicationFoldersTree]);

  const handleCopyPlanChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string): void => {
    setCopyPlan(value);
    setTitleExists(false);
    setTargetFolderId(undefined);
  };

  if (!open) return null;

  const isFetchingFolders = foldersQuery.isLoading;
  const isFetchingPublicationsTree = pubFoldersQuery.isLoading;

  return (
    <div data-test="copy-story-modal">
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="copy-story-dialog-title">
        <DialogTitle id="copy-story-dialog-title" sx={styles.dialogTitle}>
          {messages.copyStory}
          <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <Box sx={styles.nameBlock} data-test="copy-story-title">
            Copying <strong>{title}</strong>
          </Box>

          <Box sx={styles.formItem}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={styles.formLabel}>{messages.copyTo}</FormLabel>
              <RadioGroup
                value={copyPlan}
                onChange={handleCopyPlanChange}
                data-test="copy-radio-group"
                row
              >
                <FormControlLabel
                  value={COPY_PLAN_TO_FOLDER}
                  control={<Radio size="small" inputProps={{ className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>} />}
                  label={messages.copyToFolder}
                  sx={styles.radioButton}
                  data-test="copy-plan-to-folder"
                />
                <FormControlLabel
                  value={COPY_PLAN_TO_PUBLICATION}
                  control={<Radio size="small" inputProps={{ className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>} />}
                  label={messages.copyToPublication}
                  sx={styles.radioButton}
                  data-test="copy-plan-to-publication"
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <Box sx={styles.formItem}>
            <Typography variant="body2" sx={styles.formLabel}>
              {messages.newStoryName}
            </Typography>
            <TextField
              size="small"
              sx={styles.textField}
              value={storyTitle}
              onChange={(e) => setStoryTitle(e.target.value)}
              error={titleExists}
              inputProps={{ maxLength: 60, name: 'Title', className: 'no-format', 'data-test': 'new-story-title-field' }}
            />
            {titleExists && (
              <FormHelperText error>{messages.storyTitleExists}</FormHelperText>
            )}
          </Box>

          {copyPlan === COPY_PLAN_TO_FOLDER && (
            <Box sx={styles.listItem}>
              <Typography variant="body2" sx={styles.formLabel}>
                {messages.folderLocation}
              </Typography>
              <Box sx={styles.accordionContainer}>
                <FolderTreeSelect
                  activeNodeId={targetFolderId}
                  data={folderStructure}
                  dataTest="folder-accordion-select"
                  disabled={isFetchingFolders && !folderStructure.length}
                  maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
                  onNodeSelect={(node) => setTargetFolderId(node.id)}
                  showFilter={true}
                  filterPlaceholder={messages.filterFolders}
                />
                {isFetchingFolders && <CircularProgress size={20} sx={styles.foldersLoading} />}
              </Box>
            </Box>
          )}

          {copyPlan === COPY_PLAN_TO_PUBLICATION && (
            <Box sx={styles.listItem}>
              <Typography variant="body2" sx={styles.formLabel}>
                {messages.publicationLocation}
              </Typography>
              <Box sx={styles.accordionContainer}>
                <FolderTreeSelect
                  activeNodeId={targetFolderId}
                  data={publicationFoldersTree}
                  dataTest="folder-accordion-select"
                  disabled={isFetchingPublicationsTree && !publicationFoldersTree.length}
                  maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
                  onNodeSelect={(node) => {
                    const isPublicationFolders = !!publicationFoldersTree.filter((folder) => folder.id === node.id).length;
                    return isPublicationFolders ? setTargetFolderId(undefined) : setTargetFolderId(node.id);
                  }}
                  showFilter={true}
                  filterPlaceholder={messages.filterFolders}
                />
                {isFetchingPublicationsTree && <CircularProgress size={20} sx={styles.foldersLoading} />}
              </Box>
              {currentPublicationName && (
                <Typography variant="body2" sx={styles.publicationName}>
                  {messages.publication}: {currentPublicationName}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button variant="outlined" color="secondary" size="small" onClick={closeModal} data-test="cancel-copy-story">
            {messages.cancel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            data-test="confirm-copy-story"
            disabled={!targetFolderId || !storyTitle.trim().length || titleExists || isCopying}
            onClick={handleSubmit}
          >
            {messages.copy}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
